//! Helpers for referencing a native (Excel-style) chart. Charts are referred
//! to by their display name (e.g. `Chart 1`) where possible; the anchor
//! cell's A1 position is the fallback for unnamed charts (core reports both
//! to the AI).

import { sheets } from '@/app/grid/controller/Sheets';
import { ensureRectVisible } from '@/app/gridGL/interaction/viewportHelper';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { getA1Notation } from '@/app/gridGL/UI/gridHeadings/getA1Notation';
import type { JsChart } from '@/app/quadratic-core-types';

/** The anchor cell of a chart in A1 notation (from its world rect). */
export const chartAnchorA1 = (chart: JsChart): string => {
  try {
    const offsets = (sheets.getById(chart.sheet_id) ?? sheets.sheet).offsets;
    const column = offsets.getColumnFromScreen(chart.x + 1);
    const row = offsets.getRowFromScreen(chart.y + 1);
    return getA1Notation(column, row);
  } catch {
    return '';
  }
};

/** The chart's anchor cell reference including sheet name (e.g. `Sheet1!A1`). */
export const chartReferenceWithSheetName = (chart: JsChart): string => {
  const anchor = chartAnchorA1(chart);
  if (!anchor) return '';
  const sheet = sheets.getById(chart.sheet_id) ?? sheets.sheet;
  return `${sheet.name}!${anchor}`;
};

/** Finds a chart on the current sheet by its display name (case-insensitive,
 * like Excel's name box / goto). */
export const findChartByName = (name: string): JsChart | undefined => {
  const target = name.trim().toLowerCase();
  if (!target) return undefined;
  return content.chartsLayer.currentSheetCharts().find((chart) => chart.name?.toLowerCase() === target);
};

/** Finds a chart by id on any sheet. */
export const findChartById = (id: string): JsChart | undefined => content.chartsLayer.getChart(id);

/** Selects a chart and scrolls the viewport so its cell span is visible. */
export const selectAndShowChart = (chart: JsChart) => {
  try {
    const offsets = (sheets.getById(chart.sheet_id) ?? sheets.sheet).offsets;
    const min = {
      x: offsets.getColumnFromScreen(chart.x + 1),
      y: offsets.getRowFromScreen(chart.y + 1),
    };
    const max = {
      x: offsets.getColumnFromScreen(chart.x + chart.w),
      y: offsets.getRowFromScreen(chart.y + chart.h),
    };
    ensureRectVisible(chart.sheet_id, min, max);
  } catch {
    // scrolling is best-effort; still select the chart
  }
  content.chartsLayer.select(chart.id);
};
